import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { formatWalletBalanceMinor } from '../../services/channelService';
import { listWallets, postWalletCashTransfer } from '../../services/walletPublicService';
import { getAxiosErrorMessage, getWelcomeGreeting, logoutRemote } from '../../services/authService';
import { parseInrMajorToMinor, parseWalletIdFromQrText } from '../../utils/walletTransfer';
import { useToast } from '../../context/ToastContext';

const SCANNER_ELEMENT_ID = 'wallet-transfer-qr-reader';

function WalletTransferPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const toWalletId = searchParams.get('to')?.trim() ?? '';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromWalletId, setFromWalletId] = useState('');
  const [amountInr, setAmountInr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listWallets();
      const list = Array.isArray(data.wallets) ? data.wallets : [];
      setRows(list);
    } catch (e) {
      console.error(e);
      setError(getAxiosErrorMessage(e, 'Failed to load wallets'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cashWallets = useMemo(
    () => rows.filter((w) => String(w?.currency ?? '').trim().toUpperCase() === 'CASH'),
    [rows]
  );

  useEffect(() => {
    if (fromWalletId) return;
    if (cashWallets.length === 1) setFromWalletId(cashWallets[0].wallet_id);
  }, [cashWallets, fromWalletId]);

  const applyToWallet = useCallback(
    (id) => {
      const t = String(id ?? '').trim();
      setSearchParams(t ? { to: t } : {}, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!scanOpen) return undefined;

    const html5QrCode = new Html5Qrcode(SCANNER_ELEMENT_ID);
    let cancelled = false;

    const safeStop = async () => {
      try {
        await html5QrCode.stop();
        html5QrCode.clear?.();
      } catch {
        /* Already stopped, never started, or closed — html5-qrcode throws otherwise */
      }
    };

    html5QrCode
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decodedText) => {
          const id = parseWalletIdFromQrText(decodedText);
          if (!id) {
            toast.error('Could not read a wallet ID from this QR code.');
            return;
          }
          applyToWallet(id);
          toast.success('Wallet ID captured from QR.');
          void safeStop().finally(() => {
            if (!cancelled) setScanOpen(false);
          });
        },
        () => {}
      )
      .then(() => {
        if (cancelled) void safeStop();
      })
      .catch((e) => {
        console.error(e);
        toast.error('Camera could not start. Check permissions or try manual entry.');
        if (!cancelled) setScanOpen(false);
      });

    return () => {
      cancelled = true;
      void safeStop();
    };
  }, [scanOpen, applyToWallet, toast]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutRemote();
    } finally {
      setLoggingOut(false);
      navigate('/login', { replace: true });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const from = fromWalletId.trim();
    const to = toWalletId.trim();
    if (!from) {
      toast.error('Choose a cash-credit wallet to transfer from.');
      return;
    }
    if (!to) {
      toast.error('Enter or scan the recipient wallet ID.');
      return;
    }
    if (from === to) {
      toast.error('Recipient must be different from the sending wallet.');
      return;
    }
    const amt = parseInrMajorToMinor(amountInr, { allowZero: false });
    if (!amt.ok) {
      toast.error(amt.message);
      return;
    }
    setSubmitting(true);
    try {
      const result = await postWalletCashTransfer(from, {
        amount_minor: amt.value,
        to_wallet_id: to,
      });
      const key = result?.idempotency_key;
      toast.success(key ? `Credit transfer submitted. Reference: ${key}` : 'Credit transfer submitted.');
      setAmountInr('');
      load();
    } catch (err) {
      toast.error(getAxiosErrorMessage(err, 'Credit transfer failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <header className="mb-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">{getWelcomeGreeting()}</p>
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
          >
            <i className={`material-icons-outlined text-base ${loggingOut ? 'animate-spin' : ''}`}>
              {loggingOut ? 'refresh' : 'logout'}
            </i>
            {loggingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>
        <Link to="/wallets" className="link-back mb-2">
          <i className="material-icons-outlined text-base">arrow_back</i>
          All wallets
        </Link>
        <div className="flex gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg shadow-emerald-500/25">
            <i className="material-icons-outlined text-3xl">currency_exchange</i>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Cash credit transfer</p>
            <h1 className="page-title">Transfer credit</h1>
            <p className="page-subtitle">
              COD orders use cash credit on the books while physical cash is collected from the customer. Whoever receives
              that physical cash should transfer credit from their wallet to the person who handed it in—scan their QR or
              paste their wallet ID, then choose your cash-credit wallet (currency CASH) and amount. The chain continues
              until funds reach the company&apos;s account; then the company issues repayment on the wallet. Each step runs
              on the credit line issued to that role.
            </p>
          </div>
        </div>
      </header>

      <div className="card-surface p-6">
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        )}
        {!loading && error && (
          <div className="flex items-start gap-3 border-l-4 border-red-500 bg-red-50 p-4 text-red-800">
            <i className="material-icons-outlined shrink-0">error_outline</i>
            <p className="text-sm">{error}</p>
          </div>
        )}
        {!loading && !error && cashWallets.length === 0 && (
          <p className="text-sm text-slate-600">
            You don&apos;t have a cash-credit wallet (currency CASH). Credit transfers only run between these accounts.
          </p>
        )}
        {!loading && !error && cashWallets.length > 0 && (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label htmlFor="from-wallet" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                From (cash credit)
              </label>
              <select
                id="from-wallet"
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                value={fromWalletId}
                onChange={(ev) => setFromWalletId(ev.target.value)}
                required
              >
                <option value="">Select wallet</option>
                {cashWallets.map((w) => {
                  const c = w.currency != null && String(w.currency).trim() !== '' ? String(w.currency).trim() : 'INR';
                  return (
                    <option key={w.wallet_id} value={w.wallet_id}>
                      {w.wallet_id} — {formatWalletBalanceMinor(w.balance, c)} available
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label htmlFor="to-wallet" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Recipient wallet ID
                </label>
                <button
                  type="button"
                  onClick={() => setScanOpen(true)}
                  className="btn-secondary inline-flex items-center gap-1 py-1 pl-2 pr-2.5 text-xs"
                >
                  <i className="material-icons-outlined text-sm">qr_code_scanner</i>
                  Scan QR
                </button>
              </div>
              <input
                id="to-wallet"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="Paste wallet UUID or ID"
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 font-mono text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                value={toWalletId}
                onChange={(ev) => applyToWallet(ev.target.value)}
              />
            </div>

            <div>
              <label htmlFor="amount-inr" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Amount (INR)
              </label>
              <input
                id="amount-inr"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="e.g. 150.50"
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                value={amountInr}
                onChange={(ev) => setAmountInr(ev.target.value)}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={submitting}>
                <i className={`material-icons-outlined text-base ${submitting ? 'animate-spin' : ''}`}>
                  {submitting ? 'refresh' : 'send'}
                </i>
                {submitting ? 'Transferring…' : 'Transfer credit'}
              </button>
            </div>
          </form>
        )}
      </div>

      {scanOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Scan recipient QR code"
        >
          <div className="card-surface max-h-[90vh] w-full max-w-md overflow-hidden p-4 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">Scan recipient QR</p>
              <button
                type="button"
                onClick={() => setScanOpen(false)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <i className="material-icons-outlined">close</i>
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-600">Point the camera at the code shown on the recipient&apos;s screen.</p>
            <div id={SCANNER_ELEMENT_ID} className="mt-3 min-h-[240px] w-full overflow-hidden rounded-lg bg-black/5" />
          </div>
        </div>
      )}
    </div>
  );
}

export default WalletTransferPage;
